// src/pages/TarjetaPresentacion.jsx - Tarjeta de Presentación Digital Alianza Social
// VERSIÓN CORREGIDA - Manejo mejorado de imágenes con CORS
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
  Dialog,
  DialogContent,
  Slide,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { getUsuarioById } from '../actions/UsuarioAction';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Transición para el modal
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TarjetaPresentacion = () => {
  const { id } = useParams();
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [imagenBase64, setImagenBase64] = useState(null); // Nuevo estado para cache de imagen
  const tarjetaRef = useRef(null);
  const openDownloadMenu = Boolean(anchorEl);

  useEffect(() => {
    cargarUsuario();
  }, [id]);

  // Nuevo useEffect para pre-cargar la imagen en base64
  useEffect(() => {
    if (usuario && usuario.imagen) {
      precargarImagenBase64(usuario.imagen);
    }
  }, [usuario]);

  const cargarUsuario = async () => {
    try {
      setLoading(true);
      const response = await getUsuarioById(id);
      if (response && response.data) {
        setUsuario(response.data);
      }
    } catch (error) {
      console.error('Error cargando usuario:', error);
    } finally {
      setLoading(false);
    }
  };

  // Nueva función para pre-cargar la imagen
  const precargarImagenBase64 = async (urlImagen) => {
    console.log('🔄 Pre-cargando imagen...', urlImagen);
    
    try {
      // Si ya es base64, usar directamente
      if (urlImagen.startsWith('data:')) {
        setImagenBase64(urlImagen);
        console.log('✅ Imagen ya está en base64');
        return;
      }

      // Intentar cargar la imagen con crossOrigin
      const img = await cargarImagenConCrossOrigin(urlImagen);
      
      if (img) {
        // Convertir a base64 usando canvas
        const base64 = await convertirImagenABase64ViaCanvas(img);
        if (base64) {
          setImagenBase64(base64);
          console.log('✅ Imagen convertida y cacheada en base64');
        } else {
          console.log('⚠️ No se pudo convertir, usando URL original');
          setImagenBase64(urlImagen);
        }
      } else {
        console.log('⚠️ No se pudo cargar, usando URL original');
        setImagenBase64(urlImagen);
      }
    } catch (error) {
      console.error('❌ Error pre-cargando imagen:', error);
      setImagenBase64(urlImagen);
    }
  };

  // Función helper para cargar imagen con crossOrigin
  const cargarImagenConCrossOrigin = (url) => {
    return new Promise((resolve) => {
      // Si ya es base64, resolver inmediatamente
      if (url.startsWith('data:')) {
        const img = new Image();
        img.src = url;
        img.onload = () => resolve(img);
        return;
      }

      // Intentar cargar con crossOrigin
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log('✓ Imagen cargada con crossOrigin');
        resolve(img);
      };
      
      img.onerror = () => {
        console.log('✗ Fallo con crossOrigin, intentando sin él...');
        // Fallback: intentar sin crossOrigin
        const imgFallback = new Image();
        imgFallback.onload = () => {
          console.log('✓ Imagen cargada sin crossOrigin');
          resolve(imgFallback);
        };
        imgFallback.onerror = () => {
          console.log('✗ Error total cargando imagen');
          resolve(null);
        };
        imgFallback.src = url;
      };
      
      // Agregar timestamp para evitar cache
      img.src = url.includes('?') ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`;
    });
  };

  // Función para convertir imagen a base64 usando canvas
  const convertirImagenABase64ViaCanvas = async (imgElement) => {
    try {
      if (!imgElement) return null;

      // Si ya es base64, retornar
      if (imgElement.src && imgElement.src.startsWith('data:')) {
        return imgElement.src;
      }

      // Crear canvas temporal
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = imgElement.naturalWidth || imgElement.width || 300;
      canvas.height = imgElement.naturalHeight || imgElement.height || 300;
      
      // Dibujar imagen en canvas
      ctx.drawImage(imgElement, 0, 0);
      
      // Convertir a base64
      try {
        const base64 = canvas.toDataURL('image/png', 1.0);
        console.log('✓ Conversión a base64 exitosa');
        return base64;
      } catch (e) {
        console.log('✗ Error CORS al convertir canvas:', e.message);
        return null;
      }
    } catch (error) {
      console.log('✗ Error en conversión via canvas:', error.message);
      return null;
    }
  };

  const handleClickDescargar = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDownloadMenu = () => {
    setAnchorEl(null);
  };

  const descargarComoPNG = async () => {
    handleCloseDownloadMenu();
    setDownloading(true);

    try {
      const elementoOriginal = tarjetaRef.current;
      if (!elementoOriginal) {
        alert('Error: No se pudo encontrar la tarjeta');
        setDownloading(false);
        return;
      }

      console.log('📸 Iniciando captura PNG...');
      console.log('🖼️ Usando imagen:', imagenBase64 ? 'base64 cacheada' : 'URL original');

      // NUEVA ESTRATEGIA: Crear elemento temporal con imagen correcta
      console.log('🔄 Creando elemento temporal para captura...');
      
      const elementoTemporal = elementoOriginal.cloneNode(true);
      elementoTemporal.style.position = 'absolute';
      elementoTemporal.style.left = '-9999px';
      elementoTemporal.style.top = '0';
      elementoTemporal.style.transform = 'none';
      elementoTemporal.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3)';
      elementoTemporal.removeAttribute('data-tarjeta'); // Evitar duplicados
      
      // Reemplazar imagen en el clon temporal
      if (imagenBase64) {
        const tempImg = elementoTemporal.querySelector('img');
        if (tempImg) {
          console.log('🔄 Configurando imagen base64 en elemento temporal...');
          tempImg.src = imagenBase64;
          tempImg.crossOrigin = 'anonymous';
        }
      }
      
      // Agregar al DOM temporalmente
      document.body.appendChild(elementoTemporal);
      
      // Esperar a que la imagen se cargue en el elemento temporal
      const tempImg = elementoTemporal.querySelector('img');
      if (tempImg) {
        await new Promise((resolve) => {
          if (tempImg.complete) {
            console.log('✅ Imagen en elemento temporal ya cargada');
            resolve();
          } else {
            tempImg.onload = () => {
              console.log('✅ Imagen en elemento temporal cargada');
              resolve();
            };
            tempImg.onerror = () => {
              console.log('⚠️ Error cargando imagen en temporal, continuando...');
              resolve();
            };
            setTimeout(resolve, 200); // Timeout de seguridad
          }
        });
      }

      console.log('📸 Capturando elemento temporal...');
      
      // Capturar el elemento temporal
      const canvas = await html2canvas(elementoTemporal, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: elementoTemporal.scrollWidth,
        windowHeight: elementoTemporal.scrollHeight,
      });

      // Eliminar elemento temporal
      document.body.removeChild(elementoTemporal);
      console.log('🗑️ Elemento temporal eliminado');

      console.log('✅ Captura completada, generando archivo...');

      // Crear canvas con padding
      const padding = 40;
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      
      finalCanvas.width = canvas.width + (padding * 2);
      finalCanvas.height = canvas.height + (padding * 2);
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, padding, padding);

      // Descargar
      finalCanvas.toBlob((blob) => {
        if (!blob) {
          alert('Error al crear la imagen');
          setDownloading(false);
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const nombreArchivo = `tarjeta_${usuario.nombre}_${usuario.apellido}_alianza_social.png`;
        
        link.href = url;
        link.download = nombreArchivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        console.log('✅ Descarga PNG completada');
        setDownloading(false);
      }, 'image/png');

    } catch (error) {
      console.error('❌ Error al descargar PNG:', error);
      
      // Limpiar cualquier elemento temporal que pueda haber quedado
      const elementosTemporales = document.querySelectorAll('[style*="-9999px"]');
      elementosTemporales.forEach(el => {
        try {
          document.body.removeChild(el);
        } catch (e) {}
      });
      
      alert(`Error al generar la imagen: ${error.message}. Por favor, intenta nuevamente.`);
      setDownloading(false);
    }
  };

  const descargarComoPDF = async () => {
    handleCloseDownloadMenu();
    setDownloading(true);

    try {
      const elementoOriginal = tarjetaRef.current;
      if (!elementoOriginal) {
        alert('Error: No se pudo encontrar la tarjeta');
        setDownloading(false);
        return;
      }

      console.log('📄 Iniciando captura PDF...');
      console.log('🖼️ Usando imagen:', imagenBase64 ? 'base64 cacheada' : 'URL original');

      // ESTRATEGIA: Crear elemento temporal con imagen correcta
      console.log('🔄 Creando elemento temporal para captura...');
      
      const elementoTemporal = elementoOriginal.cloneNode(true);
      elementoTemporal.style.position = 'absolute';
      elementoTemporal.style.left = '-9999px';
      elementoTemporal.style.top = '0';
      elementoTemporal.style.transform = 'none';
      elementoTemporal.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3)';
      elementoTemporal.removeAttribute('data-tarjeta');
      
      // Reemplazar imagen en el clon temporal
      if (imagenBase64) {
        const tempImg = elementoTemporal.querySelector('img');
        if (tempImg) {
          console.log('🔄 Configurando imagen base64 en elemento temporal...');
          tempImg.src = imagenBase64;
          tempImg.crossOrigin = 'anonymous';
        }
      }
      
      // Agregar al DOM temporalmente
      document.body.appendChild(elementoTemporal);
      
      // Esperar a que la imagen se cargue
      const tempImg = elementoTemporal.querySelector('img');
      if (tempImg) {
        await new Promise((resolve) => {
          if (tempImg.complete) {
            console.log('✅ Imagen en elemento temporal ya cargada');
            resolve();
          } else {
            tempImg.onload = () => {
              console.log('✅ Imagen en elemento temporal cargada');
              resolve();
            };
            tempImg.onerror = () => {
              console.log('⚠️ Error cargando imagen en temporal, continuando...');
              resolve();
            };
            setTimeout(resolve, 200);
          }
        });
      }

      console.log('📸 Capturando elemento temporal...');
      
      // Capturar el elemento temporal
      const canvas = await html2canvas(elementoTemporal, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: elementoTemporal.scrollWidth,
        windowHeight: elementoTemporal.scrollHeight,
      });

      // Eliminar elemento temporal
      document.body.removeChild(elementoTemporal);
      console.log('🗑️ Elemento temporal eliminado');

      console.log('✅ Captura completada, generando PDF...');

      // Crear canvas con padding
      const padding = 40;
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      
      finalCanvas.width = canvas.width + (padding * 2);
      finalCanvas.height = canvas.height + (padding * 2);
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, padding, padding);

      const imgData = finalCanvas.toDataURL('image/png');
      
      // Calcular dimensiones para el PDF
      const imgWidth = finalCanvas.width;
      const imgHeight = finalCanvas.height;
      const pdfWidth = 210;
      const pdfHeight = (imgHeight * pdfWidth) / imgWidth;
      
      const pdf = new jsPDF({
        orientation: pdfHeight > pdfWidth ? 'portrait' : 'landscape',
        unit: 'mm',
        format: [pdfWidth, pdfHeight]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      const nombreArchivo = `tarjeta_${usuario.nombre}_${usuario.apellido}_alianza_social.pdf`;
      pdf.save(nombreArchivo);

      console.log('✅ Descarga PDF completada');
      setDownloading(false);

    } catch (error) {
      console.error('❌ Error al descargar PDF:', error);
      
      // Limpiar cualquier elemento temporal que pueda haber quedado
      const elementosTemporales = document.querySelectorAll('[style*="-9999px"]');
      elementosTemporales.forEach(el => {
        try {
          document.body.removeChild(el);
        } catch (e) {}
      });
      
      alert(`Error al generar el PDF: ${error.message}. Por favor, intenta nuevamente.`);
      setDownloading(false);
    }
  };

  const handleCompartir = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${usuario?.nombre} ${usuario?.apellido} - Alianza Social`,
          text: 'Conoce mi tarjeta de presentación en Alianza Social',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error compartiendo:', error);
      }
    } else {
      // Fallback: copiar al portapapeles
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiado al portapapeles');
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Componente reutilizable para el contenido de la tarjeta
  const TarjetaContenido = ({ inModal = false }) => (
    <Box 
      ref={!inModal ? tarjetaRef : null}
      data-tarjeta="true"
      sx={{
        backgroundColor: 'white',
        borderRadius: inModal ? '20px' : '24px',
        overflow: inModal ? 'auto' : 'hidden',
        boxShadow: inModal ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.3)',
        transform: inModal ? 'none' : 'perspective(1000px) rotateX(2deg)',
        transition: 'transform 0.3s ease',
        maxHeight: inModal ? 'calc(90vh - 60px)' : 'none',
        '&:hover': {
          transform: inModal ? 'none' : 'perspective(1000px) rotateX(0deg) scale(1.02)'
        }
      }}>
      {/* Header con gradiente */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 50%, #E63946 100%)',
        padding: inModal ? { xs: '20px 15px 15px 15px', sm: '30px 20px 20px 20px' } : { xs: '30px 20px 20px 20px', sm: '40px 30px 30px 30px' },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width=\\"60\\" height=\\"60\\" viewBox=\\"0 0 60 60\\" xmlns=\\"http://www.w3.org/2000/svg\\"%3E%3Cg fill=\\"none\\" fill-rule=\\"evenodd\\"%3E%3Cg fill=\\"%23ffffff\\" fill-opacity=\\"0.05\\"%3E%3Cpath d=\\"M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }
      }}>
        {/* Logo de Alianza Social */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: inModal ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 3 },
          position: 'relative',
          zIndex: 1
        }}>
          <Typography variant={inModal ? 'h6' : 'h5'} sx={{
            color: 'white',
            fontWeight: 800,
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
            letterSpacing: '1px',
            fontSize: inModal ? { xs: '1rem', sm: '1.25rem' } : { xs: '1.25rem', sm: '1.5rem' }
          }}>
            ⭐ ALIANZA SOCIAL
          </Typography>
        </Box>

        {/* Foto de perfil - USAR IMAGEN CACHEADA */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <Avatar
            src={imagenBase64 || usuario.imagen || 'https://tottope.vteximg.com.br/arquivos/ids/167188-1000-1000/PILIGRAM-H-1810-V07_A.png?v=636723781789170000'}
            crossOrigin="anonymous"
            sx={{
              width: inModal ? { xs: 100, sm: 120 } : { xs: 120, sm: 150 },
              height: inModal ? { xs: 100, sm: 120 } : { xs: 120, sm: 150 },
              border: inModal ? { xs: '4px solid white', sm: '5px solid white' } : '6px solid white',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
            }}
          />
        </Box>
      </Box>

      {/* Contenido de la tarjeta */}
      <Box sx={{ 
        padding: inModal ? { xs: '15px', sm: '20px' } : { xs: '20px', sm: '30px' }
      }}>
        {/* Nombre y roles */}
        <Box sx={{ textAlign: 'center', mb: inModal ? 2 : 3 }}>
          <Typography variant={inModal ? 'h5' : 'h4'} sx={{
            fontWeight: 700,
            color: '#424242',
            mb: 1,
            fontSize: inModal ? { xs: '1.25rem', sm: '1.5rem' } : { xs: '1.5rem', sm: '2rem' }
          }}>
            {usuario.nombre} {usuario.apellido}
          </Typography>

          {/* Roles/Chips */}
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap', mb: inModal ? 1 : 2 }}>
            {usuario.roles && usuario.roles.map((rol, idx) => (
              <Chip
                key={idx}
                label={rol}
                size={inModal ? 'small' : 'medium'}
                sx={{
                  backgroundColor: rol.toLowerCase().includes('candidato') ? '#E63946' : '#1E88E5',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: inModal ? { xs: '0.7rem', sm: '0.75rem' } : '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  height: inModal ? { xs: '24px', sm: '28px' } : 'auto'
                }}
              />
            ))}
          </Box>

          {/* Documento */}
          {usuario.documento && (
            <Typography variant="body2" color="text.secondary" sx={{
              fontSize: inModal ? { xs: '0.75rem', sm: '0.875rem' } : '0.875rem'
            }}>
              {usuario.tipoDocumento} {usuario.documento}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: inModal ? 1.5 : 3 }} />

        {/* Información de contacto */}
        <Box sx={{ mb: inModal ? 1.5 : 3 }}>
          {usuario.phoneNumber && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: inModal ? 1 : 2 }}>
              <Box sx={{
                width: inModal ? { xs: 32, sm: 36 } : 40,
                height: inModal ? { xs: 32, sm: 36 } : 40,
                borderRadius: '10px',
                backgroundColor: 'rgba(30, 136, 229, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: inModal ? 1.5 : 2,
                flexShrink: 0
              }}>
                <PhoneIcon sx={{ 
                  color: '#1E88E5',
                  fontSize: inModal ? { xs: '1rem', sm: '1.25rem' } : '1.5rem'
                }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{
                  fontSize: inModal ? { xs: '0.65rem', sm: '0.75rem' } : '0.75rem'
                }}>
                  Teléfono
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{
                  fontSize: inModal ? { xs: '0.8rem', sm: '0.9rem' } : '1rem'
                }}>
                  {usuario.phoneNumber}
                </Typography>
              </Box>
            </Box>
          )}

          {usuario.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: inModal ? 1 : 2 }}>
              <Box sx={{
                width: inModal ? { xs: 32, sm: 36 } : 40,
                height: inModal ? { xs: 32, sm: 36 } : 40,
                borderRadius: '10px',
                backgroundColor: 'rgba(230, 57, 70, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: inModal ? 1.5 : 2,
                flexShrink: 0
              }}>
                <EmailIcon sx={{ 
                  color: '#E63946',
                  fontSize: inModal ? { xs: '1rem', sm: '1.25rem' } : '1.5rem'
                }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{
                  fontSize: inModal ? { xs: '0.65rem', sm: '0.75rem' } : '0.75rem'
                }}>
                  Email
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{
                  wordBreak: 'break-word',
                  fontSize: inModal ? { xs: '0.8rem', sm: '0.9rem' } : '1rem'
                }}>
                  {usuario.email}
                </Typography>
              </Box>
            </Box>
          )}

          {(usuario.municipio || usuario.departamento) && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: inModal ? 1 : 2 }}>
              <Box sx={{
                width: inModal ? { xs: 32, sm: 36 } : 40,
                height: inModal ? { xs: 32, sm: 36 } : 40,
                borderRadius: '10px',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: inModal ? 1.5 : 2,
                flexShrink: 0
              }}>
                <LocationIcon sx={{ 
                  color: '#4CAF50',
                  fontSize: inModal ? { xs: '1rem', sm: '1.25rem' } : '1.5rem'
                }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{
                  fontSize: inModal ? { xs: '0.65rem', sm: '0.75rem' } : '0.75rem'
                }}>
                  Ubicación
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{
                  fontSize: inModal ? { xs: '0.8rem', sm: '0.9rem' } : '1rem'
                }}>
                  {usuario.municipio}, {usuario.departamento}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: inModal ? 1.5 : 3 }} />

        {/* Redes sociales */}
        <Box sx={{ textAlign: 'center', mb: inModal ? 1 : 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ 
            mb: inModal ? 1 : 2,
            fontSize: inModal ? { xs: '0.75rem', sm: '0.85rem' } : '0.875rem'
          }}>
            Síguenos en redes sociales
          </Typography>
          <Box sx={{ display: 'flex', gap: inModal ? 0.5 : 1, justifyContent: 'center' }}>
            <IconButton
              size={inModal ? 'small' : 'medium'}
              sx={{
                backgroundColor: 'rgba(59, 89, 152, 0.1)',
                color: '#3b5998',
                padding: inModal ? { xs: '6px', sm: '8px' } : '8px',
                '&:hover': {
                  backgroundColor: '#3b5998',
                  color: 'white'
                }
              }}
              onClick={() => window.open('https://facebook.com', '_blank')}
            >
              <FacebookIcon sx={{ fontSize: inModal ? { xs: '1rem', sm: '1.25rem' } : '1.5rem' }} />
            </IconButton>
            <IconButton
              size={inModal ? 'small' : 'medium'}
              sx={{
                backgroundColor: 'rgba(29, 161, 242, 0.1)',
                color: '#1DA1F2',
                padding: inModal ? { xs: '6px', sm: '8px' } : '8px',
                '&:hover': {
                  backgroundColor: '#1DA1F2',
                  color: 'white'
                }
              }}
              onClick={() => window.open('https://twitter.com', '_blank')}
            >
              <TwitterIcon sx={{ fontSize: inModal ? { xs: '1rem', sm: '1.25rem' } : '1.5rem' }} />
            </IconButton>
            <IconButton
              size={inModal ? 'small' : 'medium'}
              sx={{
                backgroundColor: 'rgba(225, 48, 108, 0.1)',
                color: '#E1306C',
                padding: inModal ? { xs: '6px', sm: '8px' } : '8px',
                '&:hover': {
                  backgroundColor: '#E1306C',
                  color: 'white'
                }
              }}
              onClick={() => window.open('https://instagram.com', '_blank')}
            >
              <InstagramIcon sx={{ fontSize: inModal ? { xs: '1rem', sm: '1.25rem' } : '1.5rem' }} />
            </IconButton>
            <IconButton
              size={inModal ? 'small' : 'medium'}
              sx={{
                backgroundColor: 'rgba(0, 119, 181, 0.1)',
                color: '#0077b5',
                padding: inModal ? { xs: '6px', sm: '8px' } : '8px',
                '&:hover': {
                  backgroundColor: '#0077b5',
                  color: 'white'
                }
              }}
              onClick={() => window.open('https://linkedin.com', '_blank')}
            >
              <LinkedInIcon sx={{ fontSize: inModal ? { xs: '1rem', sm: '1.25rem' } : '1.5rem' }} />
            </IconButton>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{
          textAlign: 'center',
          mt: inModal ? 1.5 : 3,
          pt: inModal ? 1.5 : 3,
          borderTop: '2px solid rgba(0,0,0,0.06)'
        }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ 
            mb: 0.5,
            fontSize: inModal ? { xs: '0.65rem', sm: '0.75rem' } : '0.75rem'
          }}>
            Por un mejor futuro para Colombia
          </Typography>
          <Typography variant="caption" sx={{
            color: '#424242',
            fontWeight: 700,
            fontSize: inModal ? { xs: '0.75rem', sm: '0.85rem' } : '0.9rem'
          }}>
            MOVIMIENTO ALIANZA SOCIAL
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '100px',
        position: 'relative'
      }}>
        <CircularProgress size={60} sx={{ color: '#1E88E5' }} />
      </Box>
    );
  }

  if (!usuario) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '100px',
        position: 'relative'
      }}>
        <Typography variant="h5" color="text.primary">Usuario no encontrado</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: { xs: '80px', sm: '100px' },
        paddingBottom: { xs: '20px', sm: '40px' },
        paddingLeft: { xs: '10px', sm: '20px' },
        paddingRight: { xs: '10px', sm: '20px' },
        position: 'relative'
      }}>
        {/* Tarjeta Principal */}
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ position: 'relative' }}>
            {/* Botones de acción flotantes */}
            <Box sx={{
              position: 'absolute',
              top: { xs: -10, sm: -15 },
              right: { xs: -10, sm: -15 },
              display: 'flex',
              gap: { xs: 0.5, sm: 1 },
              zIndex: 1000
            }}>
              <Tooltip title="Ver en modal" arrow>
                <IconButton
                  onClick={handleOpenModal}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    '&:hover': {
                      backgroundColor: 'white',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  <VisibilityIcon sx={{ 
                    color: '#9C27B0',
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                  }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compartir" arrow>
                <IconButton
                  onClick={handleCompartir}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    '&:hover': {
                      backgroundColor: 'white',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  <ShareIcon sx={{ 
                    color: '#1E88E5',
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                  }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Descargar" arrow>
                <IconButton
                  onClick={handleClickDescargar}
                  disabled={downloading}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    '&:hover': {
                      backgroundColor: 'white',
                      transform: 'scale(1.1)'
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(200, 200, 200, 0.5)',
                      cursor: 'not-allowed'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  {downloading ? (
                    <CircularProgress size={20} sx={{ color: '#4CAF50' }} />
                  ) : (
                    <DownloadIcon sx={{ 
                      color: '#4CAF50',
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            {/* Contenido de la tarjeta */}
            <TarjetaContenido inModal={false} />
          </Box>
        </Container>
      </Box>

      {/* Modal con vista ampliada de la tarjeta */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            margin: { xs: '16px', sm: '32px' },
            maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' }
          }
        }}
      >
        <DialogContent sx={{ 
          padding: '0 !important',
          position: 'relative',
          overflow: 'visible'
        }}>
          {/* Botón de cerrar */}
          <Tooltip title="Cerrar" arrow>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: { xs: -10, sm: -15 },
                right: { xs: -10, sm: -15 },
                zIndex: 1001,
                backgroundColor: '#E63946',
                color: 'white',
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                boxShadow: '0 4px 12px rgba(230, 57, 70, 0.4)',
                '&:hover': {
                  backgroundColor: '#C62828',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s'
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            </IconButton>
          </Tooltip>

          {/* Tarjeta en el modal */}
          <TarjetaContenido inModal={true} />
        </DialogContent>
      </Dialog>

      {/* Menú de opciones de descarga */}
      <Menu
        anchorEl={anchorEl}
        open={openDownloadMenu}
        onClose={handleCloseDownloadMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            mt: 1
          }
        }}
      >
        <MenuItem 
          onClick={descargarComoPNG}
          disabled={downloading}
          sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: 'rgba(76, 175, 80, 0.08)'
            }
          }}
        >
          <ListItemIcon>
            <ImageIcon sx={{ color: '#4CAF50' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Descargar como PNG"
            secondary="Imagen de alta calidad"
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: '0.95rem'
            }}
            secondaryTypographyProps={{
              fontSize: '0.75rem'
            }}
          />
        </MenuItem>
        
        <Divider sx={{ my: 0.5 }} />
        
        <MenuItem 
          onClick={descargarComoPDF}
          disabled={downloading}
          sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: 'rgba(230, 57, 70, 0.08)'
            }
          }}
        >
          <ListItemIcon>
            <PdfIcon sx={{ color: '#E63946' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Descargar como PDF"
            secondary="Documento portable"
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: '0.95rem'
            }}
            secondaryTypographyProps={{
              fontSize: '0.75rem'
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default TarjetaPresentacion;